import baseApi from './base.api'
import {
    normalizeCreateConceptualLabelResponse,
    normalizeFetchConceptualLabelsResponse,
    normalizeUpdateConceptualLabelResponse,
    normalizeFetchConceptualLabelDiscoveryResponse,
} from './utils/normalizers/conceptual-label.normalizers'
import {
    handleDeleteOnQueryStarted,
} from './utils/response-handlers/manualCacheUpdateUtils'
import { handleDeletePatternOnQueryStarted } from './utils/response-handlers/discovery.api.response.handlers'
import { CONCEPTUAL_LABEL_TAG, createTagsForList } from './utils/tags'
import { createSearchParams } from '../../utils/request.helper'
import { flatten } from '../../utils/array.helper'
import { denormalizeConceptualLabelsMatches } from '../../pages/resources/conceptual-labels/select/ConceptualLabelSelect'

export const conceptualLabelApi = baseApi.injectEndpoints({
    endpoints: builder => ({
        fetchConceptualLabels: builder.query({
            query: () => ({
                url: `/conceptual-labels/`,
                method: 'GET',
            }),
            transformResponse: normalizeFetchConceptualLabelsResponse,
            providesTags: result => createTagsForList(flatten(result), CONCEPTUAL_LABEL_TAG),
        }),
        fetchConceptualLabelsForVersion: builder.query({
            query: ({ token, organizationOnly = false } = {}) => ({
                url: `/conceptual-labels/${token}/by-version`,
                method: 'GET',
                params: {
                    organization_only: organizationOnly
                },
            }),
            transformResponse: normalizeFetchConceptualLabelsResponse,
            providesTags: result => createTagsForList(flatten(result), CONCEPTUAL_LABEL_TAG),
        }),
        fetchConceptualLabelDiscovery: builder.query({
            query: ({ token, disciplines }) => ({
                url: `/conceptual-label-discovery/${token}`,
                method: 'GET',
                params: createSearchParams({ disciplines }),
            }),
            providesTags: [{ type: CONCEPTUAL_LABEL_TAG, id: 'DISCOVERY' }],
            transformResponse: normalizeFetchConceptualLabelDiscoveryResponse,
        }),
        fetchAllConceptualLabelDiscovery: builder.query({
            query: ({ token }) => ({
                url: `/conceptual-label-discovery/${token}/all`,
                method: 'GET',
            }),
            providesTags: [{ type: CONCEPTUAL_LABEL_TAG, id: 'DISCOVERY' }],
            transformResponse: normalizeFetchConceptualLabelDiscoveryResponse,
        }),
        createConceptualLabel: builder.mutation({
            query: ({ name, disciplineId, source, description, parentId, typeId }) => ({
                url: `/conceptual-labels/`,
                method: 'POST',
                body: { name, disciplines: [disciplineId], source, description, parent_id: parentId, type_id: typeId },
            }),
            transformResponse: normalizeCreateConceptualLabelResponse,
            invalidatesTags: [{ type: CONCEPTUAL_LABEL_TAG, id: 'LIST' }],
        }),
        updateConceptualLabel: builder.mutation({
            query: ({ id, name, disciplineId, source, description, parentId, typeId }) => ({
                url: `/conceptual-labels/${id}`,
                method: 'PATCH',
                body: { name, disciplines: [disciplineId], source, description, parent_id: parentId, type_id: typeId },
            }),
            transformResponse: normalizeUpdateConceptualLabelResponse,
            invalidatesTags: (result, error, { id }) => [{ type: CONCEPTUAL_LABEL_TAG, id }],
        }),
        deleteConceptualLabel: builder.mutation({
            query: ({ id }) => ({
                url: `/conceptual-labels/${id}`,
                method: 'DELETE',
            }),
            onQueryStarted: handleDeleteOnQueryStarted('fetchConceptualLabels'),
            invalidatesTags: (result, error, { id }) => [{ type: CONCEPTUAL_LABEL_TAG, id }],
        }),
        updateConceptualLabelDiscovery: builder.mutation({
            query: ({ patternId, ...rest }) => ({
                url: `/conceptual-label-discovery/${patternId}`,
                method: 'PATCH',
                body: {
                    ...rest,
                    conceptual_labels: denormalizeConceptualLabelsMatches(rest.conceptual_labels)
                },
            }),
            invalidatesTags: [{ type: CONCEPTUAL_LABEL_TAG, id: 'DISCOVERY' }],
        }),
        deleteConceptualLabelDiscovery: builder.mutation({
            query: ({ patternId }) => ({
                url: `/conceptual-label-discovery/${patternId}`,
                method: 'DELETE',
            }),
            invalidatesTags: [{ type: CONCEPTUAL_LABEL_TAG, id: 'DISCOVERY' }],
            onQueryStarted: handleDeletePatternOnQueryStarted,
        }),
        projectConceptualLabelDiscovery: builder.mutation({
            query: ({ layerId, patternIds }) => ({
                url: `/conceptual-label-discovery/project/${layerId}`,
                method: 'POST',
                body: patternIds,
            }),
            invalidatesTags: [{ type: CONCEPTUAL_LABEL_TAG, id: 'DISCOVERY' }],
        }),
    }),
})

export const {
    useFetchConceptualLabelsQuery,
    useFetchConceptualLabelDiscoveryQuery,
    useFetchAllConceptualLabelDiscoveryQuery,
    useFetchConceptualLabelsForVersionQuery,
    useCreateConceptualLabelMutation,
    useUpdateConceptualLabelMutation,
    useDeleteConceptualLabelMutation,
    useUpdateConceptualLabelDiscoveryMutation,
    useDeleteConceptualLabelDiscoveryMutation,
    useProjectConceptualLabelDiscoveryMutation,
} = conceptualLabelApi
